import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/cn";
import { useTheme, type ThemeMode } from "@/theme/ThemeProvider";

/* --------------------------------- Button --------------------------------- */
type ButtonVariant = "coral" | "ghost" | "quiet" | "ink";
type ButtonSize = "sm" | "md" | "lg";

export function Button({
  variant = "ghost",
  size = "md",
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  const sizes: Record<ButtonSize, string> = {
    sm: "h-8 px-3 text-[13px] gap-1.5",
    md: "h-10 px-4 text-sm gap-2",
    lg: "h-12 px-6 text-[15px] gap-2",
  };
  const variants: Record<ButtonVariant, string> = {
    coral: "bg-accent text-white hover:bg-accent-hover shadow-[0_6px_18px_-8px_rgba(184,68,34,0.7)]",
    ink: "bg-ink-strong text-canvas hover:opacity-90",
    ghost: "bg-transparent text-ink border border-hairline hover:bg-paper-2 dark:hover:bg-white/5",
    quiet: "bg-transparent text-muted hover:text-ink",
  };
  return (
    <button
      className={cn(
        "inline-flex select-none items-center justify-center rounded-btn font-medium transition-all duration-200 ease-out-soft active:translate-y-px disabled:opacity-40",
        sizes[size],
        variants[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---------------------------------- Chip ---------------------------------- */
export function Chip({
  children,
  active,
  mono,
  dashed,
  onClick,
  className,
}: {
  children: ReactNode;
  active?: boolean;
  mono?: boolean;
  dashed?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-chip px-2.5 py-1 text-[12px] transition-colors",
        mono && "font-mono text-[10px] uppercase tracking-wide",
        dashed
          ? "border border-dashed border-hairline text-muted hover:text-ink"
          : active
            ? "bg-accent-soft text-accent"
            : "bg-paper-2 dark:bg-white/5 text-muted hover:text-ink",
        onClick && "cursor-pointer",
        className,
      )}
    >
      {children}
    </Comp>
  );
}

/* ------------------------------ SegmentedNav ------------------------------ */
export function SegmentedNav<T extends string>({
  items,
  value,
  onChange,
  className,
}: {
  items: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={cn("no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1", className)}>
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            onClick={() => onChange(it.value)}
            className={cn(
              "relative whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-200",
              active ? "text-ink-strong" : "text-muted hover:text-ink",
            )}
          >
            {it.label}
            {active && (
              <span className="absolute inset-x-3 -bottom-px h-[2px] rounded-full bg-accent" />
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------ ThemeToggle ------------------------------- */
export function ThemeToggle({ compact }: { compact?: boolean }) {
  const { mode, setMode, resolved } = useTheme();
  const opts: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
    { value: "light", label: "Light", icon: Sun },
    { value: "ink", label: "Ink", icon: Moon },
    { value: "auto", label: "Auto", icon: Monitor },
  ];
  if (compact) {
    const Icon = resolved === "ink" ? Sun : Moon;
    return (
      <button
        onClick={() => setMode(resolved === "ink" ? "light" : "ink")}
        aria-label="Toggle theme"
        className="grid h-10 w-10 place-items-center rounded-full border border-hairline text-muted hover:text-ink"
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
      </button>
    );
  }
  return (
    <div className="inline-flex rounded-full border border-hairline p-1">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = mode === o.value;
        return (
          <button
            key={o.value}
            onClick={() => setMode(o.value)}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] transition-colors",
              active ? "bg-paper-2 dark:bg-white/10 text-ink-strong" : "text-muted hover:text-ink",
            )}
          >
            <Icon className="h-4 w-4" strokeWidth={1.5} />
            {o.label}
            {active && <Check className="h-3 w-3 text-accent" />}
          </button>
        );
      })}
    </div>
  );
}
